// Resolved values for the Room rates tab (PRD §3.1): the first level holding a
// value wins, room × season > room > contract default. Only explicit values are
// stored; `null` / `undefined` means "inherit". `source` tells the screen which
// level the value came from ('season' | 'room' | 'contract' | 'ageGroup' |
// 'default') so it can style inherited values.

const CONTRACT_DEFAULTS = {
  minimumStay: (settings) => settings.minimumStay,
  mealPlanId: (settings) => settings.mealPlanId,
  releaseDays: (settings) => settings.releaseDays,
  allotmentCount: () => 0,
  sharedAllotmentCount: () => 0,
  freeSale: () => false,
  translations: () => [],
};

export const isSet = (value) =>
  value !== null &&
  value !== undefined &&
  !(Array.isArray(value) && value.length === 0);

export const findRoom = (model, roomId) =>
  model.rooms.find((room) => room.id === roomId);

export const findSeasonRoom = (model, seasonId, roomId) =>
  model.seasonRooms.find(
    (seasonRoom) =>
      seasonRoom.seasonId === seasonId && seasonRoom.roomId === roomId,
  );

/**
 * Room condition `field` for a room, optionally in a season (omit `seasonId`
 * for the "All seasons" scope). Fields: minimumStay, mealPlanId, releaseDays,
 * allotmentCount, sharedAllotmentCount, freeSale, translations.
 */
export const resolveRoomCondition = (model, { roomId, seasonId }, field) => {
  const seasonValue =
    seasonId === undefined
      ? undefined
      : findSeasonRoom(model, seasonId, roomId)?.[field];
  if (isSet(seasonValue)) return { value: seasonValue, source: 'season' };

  const roomValue = findRoom(model, roomId)?.[field];
  if (isSet(roomValue)) return { value: roomValue, source: 'room' };

  const contractValue = CONTRACT_DEFAULTS[field](model.settings);
  return {
    value: contractValue ?? null,
    source: field in model.settings ? 'contract' : 'default',
  };
};

/** Offered unless the room × season says otherwise. */
export const isOffered = (model, { roomId, seasonId }) =>
  findSeasonRoom(model, seasonId, roomId)?.offered !== false;

/**
 * Extra-bed price (`kind` 'fitPrice' | 'groupPrice') for an age group in a
 * room × season; falls back to the age-group default (same for F and G), or
 * Free. `isFree` is true only when the value comes from a Free age group.
 */
export const resolveExtraBedPrice = (
  model,
  { roomId, seasonId, ageGroupId },
  kind,
) => {
  const explicit = findSeasonRoom(
    model,
    seasonId,
    roomId,
  )?.extraBedPrices?.find((price) => price.ageGroupId === ageGroupId)?.[kind];
  if (isSet(explicit)) {
    return { value: explicit, source: 'season', isFree: false };
  }

  const ageGroup = model.ageGroups.find((group) => group.id === ageGroupId);
  return ageGroup?.isFree
    ? { value: null, source: 'ageGroup', isFree: true }
    : {
        value: ageGroup?.defaultPrice ?? null,
        source: 'ageGroup',
        isFree: false,
      };
};
